import { ctaCreateId, ctaSolutionNavigationId } from "@/assets/googleTrackIds";
import {
  Add,
  FiArrowLeft,
  FiArrowRight,
  FiCreditCard,
  FiDollarSign,
  FiGitCommit,
  FiGlobe,
  FiLink,
  FiPackage,
  FiPercent,
  FiPlus,
  FiShare2,
  FiUser,
  FiUserPlus,
  OpenBlank,
} from "@payping/icons";
import { Button, Card, Flex, Grid, Progress, Typography } from "@payping/web-components";
import { ReactNode, SVGProps, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector, useAppTranslation } from "@/utils";
import { DashboardRoutes } from "@/router";
import { buildUrl } from "@payping/utilities-shared/url";
import { USER_CLAIMS_LIST, useUserClaim } from "@/utils/useUserClaims";
import Style from "./style.module.scss";
import { URLS } from "@payping/utilities-shared/consts";

type TaskItem = {
  title: string;
  category: string;
  icons: ((props: SVGProps<SVGSVGElement>) => JSX.Element)[];
  description: string;
  actionLink: ReactNode;
  key: number;
  isActive: boolean;
};

type Props = {
  isLoading: boolean;
};
const Tasks = ({ isLoading }: Props) => {
  const { md } = Grid.useBreakpoint();
  const { t } = useAppTranslation();

  const [activeKey, setActiveKey] = useState<number>(0);
  const tasksStatus = useAppSelector((state) => state.user.overview.tasksStatus);

  const hideSection =
    tasksStatus?.hasCoupon &&
    tasksStatus.hasCustomer &&
    tasksStatus.hasInvoices &&
    tasksStatus.hasProduct &&
    tasksStatus.hasToken &&
    tasksStatus.hasTransaction;

  const username = useAppSelector((state) => state.user.personalInfo?.username);
  const urlWithHttps = buildUrl(`https://${URLS.BASE_URL}`, `@${username}`).toString();

  // Handling Permission And Showing Upgrade Modal
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useUserClaim([USER_CLAIMS_LIST.invoiceService]);

  const taskItems: TaskItem[] = [
    {
      actionLink: (
        <Link to={DashboardRoutes.myShop.product.create.url}>
          <Button icon={<Add width={"16px"} />} type="primary">
            {t("overview.tasks.product.action")}
          </Button>
        </Link>
      ),
      category: t("overview.tasks.product.category"),
      description: t("overview.tasks.product.description"),
      icons: [FiPackage, FiPlus, FiShare2, FiArrowLeft, FiUser],
      key: 0,
      title: t("overview.tasks.product.title"),
      isActive: !tasksStatus?.hasProduct,
    },
    {
      actionLink: (
        <Button
          data-google-track={ctaCreateId}
          onClick={() => {
            if (hasPermission) {
              navigate({ pathname: DashboardRoutes.myShop.invoice.create.url }, { state: location.state });
            } else {
              navigate({ pathname: DashboardRoutes.myShop.invoice.list.url }, { state: location.state });
            }
          }}
          icon={<Add width={"16px"} />}
          type="primary"
        >
          {t("overview.tasks.factor.action")}
        </Button>
      ),
      category: t("overview.tasks.factor.category"),
      description: t("overview.tasks.factor.description"),
      icons: [FiPackage, FiPlus, FiShare2, FiArrowLeft, FiUser],
      key: 1,
      title: t("overview.tasks.factor.title"),
      isActive: !tasksStatus?.hasInvoices,
    },
    {
      actionLink: (
        <Link to={DashboardRoutes.myShop.coupon.create.url}>
          <Button icon={<Add width={"16px"} />} type="primary">
            {t("overview.tasks.off.action")}
          </Button>
        </Link>
      ),
      category: t("overview.tasks.off.category"),
      description: t("overview.tasks.off.description"),
      icons: [FiPercent],
      key: 2,
      title: t("overview.tasks.off.title"),
      isActive: !tasksStatus?.hasCoupon,
    },
    {
      actionLink: (
        <Link to={DashboardRoutes.myShop.customer.create.url}>
          <Button icon={<Add width={"16px"} />} type="primary">
            {t("overview.tasks.customer.action")}
          </Button>
        </Link>
      ),
      category: t("overview.tasks.customer.category"),
      description: t("overview.tasks.customer.description"),
      icons: [FiUserPlus],
      key: 3,
      title: t("overview.tasks.customer.title"),
      isActive: !tasksStatus?.hasCustomer,
    },
    {
      actionLink: (
        <Link to={urlWithHttps}>
          <Button icon={<OpenBlank width={14} height={14} />} type="primary">
            {t("overview.tasks.gatewayTest.action")}
          </Button>
        </Link>
      ),
      category: t("overview.tasks.gatewayTest.category"),
      description: t("overview.tasks.gatewayTest.description"),
      icons: [FiCreditCard, FiGitCommit, FiDollarSign],
      key: 4,
      title: t("overview.tasks.gatewayTest.title"),
      isActive: !tasksStatus?.hasTransaction,
    },
    {
      actionLink: (
        <Link to={DashboardRoutes.connections.developers.root.url}>
          <Button icon={<Add width={"16px"} />} type="primary">
            {t("overview.tasks.gatewayConnect.action")}
          </Button>
        </Link>
      ),
      category: t("overview.tasks.gatewayConnect.category"),
      description: t("overview.tasks.gatewayConnect.description"),
      icons: [FiCreditCard, FiLink, FiGlobe],
      key: 5,
      title: t("overview.tasks.gatewayConnect.title"),
      isActive: !tasksStatus?.hasToken,
    },
  ].filter((i) => i.isActive);

  const calcProgress = () => Math.round(((activeKey + 1) * 100) / taskItems.length);
  const goSelectNextItem = () => {
    setActiveKey((pre) => (pre === taskItems.length - 1 ? 0 : pre + 1));
  };
  const goPreviousItem = () => {
    setActiveKey((pre) => (pre === 0 ? taskItems.length - 1 : pre - 1));
  };

  const steps = (
    <Typography.Text type={"secondary"}>
      {activeKey + 1} {t("common.from")} {taskItems.length}
    </Typography.Text>
  );
  const progress = <Progress percent={calcProgress()} steps={taskItems.length} showInfo={false} />;

  if (hideSection || isLoading) return null;

  const taskProgressBarDesktop = (
    <div className="tw-absolute tw-left-0 tw-top-0 tw-m-1">
      <Flex gap="16px" align="center">
        {/* < Progress Antd > */}
        {progress}

        {/* < Shows Steps ex: 1 from 6 > */}
        {steps}

        {/* < Arrows Icon > */}
        <Flex gap="4px">
          <Button
            onClick={() => goPreviousItem()}
            data-google-track={ctaSolutionNavigationId}
            icon={<FiArrowRight width={14} height={14} />}
          />
          <Button
            onClick={() => goSelectNextItem()}
            data-google-track={ctaSolutionNavigationId}
            icon={<FiArrowLeft width={14} height={14} />}
          />
        </Flex>
      </Flex>
    </div>
  );

  if (md) {
    return (
      <>
        <Card.Ant className="w-100 relative">
          {/* < lg -> Task ProgressBar Desktop > */}
          {taskProgressBarDesktop}

          <Flex className={Style.fadeIn} gap="16px" vertical key={taskItems[activeKey].key}>
            <Typography.Text type={"secondary"} className="tw-mb-2">
              {taskItems[activeKey].category}
            </Typography.Text>

            <Typography.Title level={4} className="m-0">
              {taskItems[activeKey].title}
            </Typography.Title>
            <Flex gap="8px" wrap="nowrap">
              <Typography.Text className="color--gray500 m-0">{taskItems[activeKey].description}</Typography.Text>
              <div className="mr-auto">
                <Flex align="center" className="mr-auto" gap="8px">
                  {taskItems[activeKey].icons.map((Icon, index) => (
                    <Icon key={index} className={index % 2 === 0 ? undefined : "color--gray500"} width={"52px"} />
                  ))}
                </Flex>
              </div>
            </Flex>
            <div>{taskItems[activeKey].actionLink}</div>
          </Flex>
        </Card.Ant>
      </>
    );
  }
  return (
    <>
      <Flex vertical className={"tw-mt-3 tw-w-full"}>
        <Typography.Text type={"secondary"} className="tw-mb-3">
          {taskItems[activeKey].category}
        </Typography.Text>
      </Flex>
      <Flex wrap="nowrap" className="overflow-auto w-100" gap="16px">
        {taskItems.map((item) => (
          <Card.Ant key={item.key}>
            <Flex className={Style.fadeIn} gap="16px" style={{ width: "221px", height: "270px" }} vertical>
              <div className="mb-1">
                <Flex align="center" className="mr-auto" gap="8px">
                  {item.icons.map((Icon, index) => (
                    <Icon key={index} className={index % 2 === 0 ? undefined : "color--gray500"} width={"32px"} />
                  ))}
                </Flex>
              </div>
              <Typography.Title level={4} className="!tw-m-0">
                {item.title}
              </Typography.Title>

              <Typography.Text className="color--gray500 m-0 tw-overflow-clip">{item.description}</Typography.Text>
            </Flex>
            <div>{item.actionLink}</div>
          </Card.Ant>
        ))}
      </Flex>
    </>
  );
};

export default Tasks;
